import StyledButton from '@/components/styled/StyledButton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import HorizontalLine from '@/components/ui/HorizontalLine';
import {
  formatPrice,
  roundUpPrice,
  sanitizeDeltaPrice,
  hasNonZeroDisplayedPrice,
} from '@/utils/circuits/prices/formatPrice';
import { format } from 'date-fns';
import { SelectedEstimateOption } from '../../../types/estimates/SelectedEstimateOptionType';
import { useCallback, useMemo } from 'react';
import { BenefitType } from '@/types/benefits/benefitTypes';
import { useConvertPriceOnlyIfDateISProvided } from '@/hooks/useCurrencyExchange';
import { formatCurrency } from '@/utils/prices/formatCurrency';
import PlusIcon from '@/assets/icons/plus.svg?react';
import StyledButtonLoader from '@/components/styled/StyledButtonLoader';
import clsx from 'clsx';
import { useTranslation } from '@/i18n';
import { useLanguage } from '@/contexts/LanguageContext';
import { LOCALES_MAP } from '@/constants/localeMap';
import { getBenefitTypeTranslatedLabel } from '@/utils/benefits/getBenefitTypeName';

interface EstimateDetailsSideCardProps {
  image?: string;
  countries: string;
  title: string;
  nbDays: number;
  nbNights: number;
  arrivalDate: Date;
  departureDate: Date;
  nbAdults: number;
  nbChildren: number;
  totalPrice: number;
  internationalFlightPrice: number;
  selectedOptions: Record<string, SelectedEstimateOption>;
  mainBtnText: string;
  currency?: string;
  /** Currency of stored option deltas (EUR after estimate FX bake). */
  optionsCurrency?: string;
  onMainClick?: () => void;
  secondaryBtnText?: string;
  onSecondaryClick?: () => void;
  isSecondaryBtnDisabled?: boolean;
  creationDate?: Date;
  isCircuitPage?: boolean;
  isValid?: boolean;
  errorsLength?: number;
  /** Souplesse TO : hide the main (confirm reservation) button on the client link. */
  hideMainBtn?: boolean;
  /** Souplesse TO : highlight the confirm button (green) once the client sent his request. */
  mainBtnHighlighted?: boolean;
  /** Optional notice rendered just above the main button. */
  mainBtnNotice?: string;
  /** When set, replaces the secondary button with this text (e.g. request already sent). */
  secondaryReplacementText?: string;
  distributorMarginPriceFactor?: number;
  /** Total already includes circuit, options, flights and agency margins (frozen snapshot). */
  isAllInclusiveTotal?: boolean;
  titleMuted?: boolean;
}

const EstimateDetailsSideCard = ({
  image,
  countries,
  title,
  nbDays,
  nbNights,
  arrivalDate,
  departureDate,
  nbAdults,
  nbChildren,
  totalPrice,
  internationalFlightPrice,
  selectedOptions,
  mainBtnText,
  currency = 'EUR',
  optionsCurrency = 'EUR',
  onMainClick,
  secondaryBtnText = 'Enregistrer mon devis',
  onSecondaryClick,
  isSecondaryBtnDisabled,
  creationDate,
  isCircuitPage,
  isValid,
  errorsLength,
  hideMainBtn = false,
  mainBtnHighlighted = false,
  mainBtnNotice,
  secondaryReplacementText,
  distributorMarginPriceFactor = 1,
  isAllInclusiveTotal = false,
  titleMuted = false,
}: EstimateDetailsSideCardProps) => {
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  const convertPrice = useConvertPriceOnlyIfDateISProvided(creationDate);

  const circuitPrice = isAllInclusiveTotal
    ? totalPrice
    : totalPrice + internationalFlightPrice;

  const convertOptionDelta = useCallback(
    (deltaPrice: number) =>
      sanitizeDeltaPrice(
        (convertPrice(deltaPrice, optionsCurrency, currency) ?? deltaPrice) *
          distributorMarginPriceFactor,
      ),
    [convertPrice, currency, distributorMarginPriceFactor, optionsCurrency],
  );

  const finalPrice = useMemo(() => {
    if (isAllInclusiveTotal) return totalPrice;
    return roundUpPrice(
      circuitPrice +
        Object.values(selectedOptions).reduce(
          (acc, { deltaPrice, carCategoryOptions }) =>
            acc +
            convertOptionDelta(deltaPrice ?? 0) +
            Object.values(carCategoryOptions ?? {})
              .map((categoryElement) =>
                categoryElement
                  .filter(({ isRequired }) => !isRequired)
                  .reduce(
                    (acc, { deltaPrice }) =>
                      acc + convertOptionDelta(deltaPrice ?? 0),
                    0,
                  ),
              )
              .reduce((acc, val) => acc + val, 0),
          0,
        ),
    );
  }, [
    circuitPrice,
    convertOptionDelta,
    isAllInclusiveTotal,
    selectedOptions,
    totalPrice,
  ]);

  const pricePerOptionType: Record<BenefitType, number> = useMemo(
    () =>
      Object.values(selectedOptions).reduce(
        (acc, { deltaPrice, type, carCategoryOptions }) => {
          const additionalPrice =
            convertOptionDelta(deltaPrice ?? 0) +
            Object.values(carCategoryOptions ?? {})
              .map((categoryElement) =>
                categoryElement
                  .filter(({ isRequired }) => !isRequired) // Required options are already included in the circuit price
                  .reduce(
                    (acc, { deltaPrice }) =>
                      acc + convertOptionDelta(deltaPrice ?? 0),
                    0,
                  ),
              )
              .reduce((acc, val) => acc + val, 0);
          if (type in acc) acc[type] += additionalPrice;
          else acc[type] = additionalPrice;
          return acc;
        },
        {} as Record<BenefitType, number>,
      ),
    [convertOptionDelta, selectedOptions],
  );

  const optionsToDisplay = useMemo(
    () =>
      Object.entries(pricePerOptionType).filter(([, deltaPrice]) =>
        hasNonZeroDisplayedPrice(deltaPrice),
      ),
    [pricePerOptionType],
  );

  return (
    <Card className="flex flex-col bg-white border border-[#E2E8F0] rounded-lg min-w-64 lg:min-w-80 xl:min-w-96 h-fit">
      <CardHeader>
        <CardTitle className="flex flex-col gap-4">
          {image && (
            <img
              className="max-h-52 xl:max-h-72 object-cover object-center rounded-lg"
              src={image}
            />
          )}
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-brand-600">
              {countries}
            </span>
            <h1
              className={clsx('text-2xl font-semibold', {
                'text-gray-500': titleMuted,
                'text-gray-950': !titleMuted,
              })}
            >
              {title}
            </h1>
            <span className="text-sm font-normal text-black">
              {`${t('circuits.duration', { days: nbDays, nights: nbNights })}`}
            </span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <HorizontalLine />
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <CardLabel label={t('estimates.arrival')} />
            <CardValue
              value={format(arrivalDate, 'PPP', {
                locale: LOCALES_MAP[currentLanguage],
              })}
            />
          </div>
          <div className="flex gap-2">
            <CardLabel label={t('estimates.departure')} />
            <CardValue
              value={format(departureDate, 'PPP', {
                locale: LOCALES_MAP[currentLanguage],
              })}
            />
          </div>
          <div className="flex gap-2">
            <CardLabel label={t('estimates.adults')} />
            <CardValue value={nbAdults} />
          </div>
          {nbChildren > 0 && (
            <div className="flex gap-2">
              <CardLabel label={t('estimates.children')} />
              <CardValue value={nbChildren} />
            </div>
          )}
        </div>
        <HorizontalLine />
        <div className="flex flex-col gap-4">
          <div className="flex justify-between">
            <CardLabel label={t('estimates.details.sideCard.circuit')} />
            <CardValue
              value={`${formatPrice(circuitPrice)} ${formatCurrency(currency)}`}
            />
          </div>
          {optionsToDisplay.length > 0 && (
            <div className="flex flex-col gap-1">
              <CardLabel label={t('estimates.details.sideCard.options')} />
              {optionsToDisplay.map(
                ([type, deltaPrice], indexSelectedOption) => (
                  <div
                    className="flex justify-between"
                    key={`${type}-${indexSelectedOption}`}
                  >
                    <CardLabel
                      label={getBenefitTypeTranslatedLabel(
                        t,
                        type as BenefitType,
                      )}
                    />
                    <CardValue
                      value={`${formatPrice(deltaPrice)} ${formatCurrency(currency)}`}
                    />
                  </div>
                ),
              )}
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-base font-semibold text-brand-600">
              {t('estimates.details.sideCard.total')}
            </span>
            <span className="text-base font-semibold text-brand-600">
              {formatPrice(finalPrice)} {formatCurrency(currency)}
            </span>
          </div>
        </div>
        {isCircuitPage ? (
          onMainClick && (
            <StyledButtonLoader
              title={t('estimates.details.sideCard.publishCircuit')}
              className="font-semibold gap-1 px-3 py-2"
              onClick={onMainClick}
              successTitle={t('estimates.details.sideCard.publishedSuccess')}
              hasSucceeded={!errorsLength}
              errorTitle={t('estimates.details.sideCard.checkRequiredFields')}
              icon={
                <PlusIcon
                  className={clsx('size-5', {
                    'fill-white': isValid,
                    'fill-gray-400': !isValid,
                  })}
                />
              }
              iconPosition="right"
            />
          )
        ) : hideMainBtn ? null : (
          <>
            {mainBtnNotice && (
              <p className="text-sm font-medium text-success-700">
                {mainBtnNotice}
              </p>
            )}
            <StyledButton
              title={mainBtnText}
              className={clsx('rounded-lg', {
                '!bg-success-600 hover:!bg-success-700 !border-success-600':
                  mainBtnHighlighted,
              })}
              onClick={onMainClick}
              disabled={onMainClick === undefined}
            />
          </>
        )}
        {secondaryReplacementText ? (
          <p className="text-sm font-medium text-success-700 text-center">
            {secondaryReplacementText}
          </p>
        ) : (
          onSecondaryClick != undefined && (
            <StyledButton
              variant="outlined"
              title={secondaryBtnText}
              onClick={onSecondaryClick}
              disabled={
                isSecondaryBtnDisabled !== undefined
                  ? isSecondaryBtnDisabled
                  : !hideMainBtn && onMainClick === undefined
              }
            />
          )
        )}
      </CardContent>
    </Card>
  );
};

export default EstimateDetailsSideCard;

const CardLabel = ({ label }: { label: string }) => (
  <span className="text-sm leading-6 font-semibold text-black">{label}</span>
);

const CardValue = ({ value }: { value: string | number }) => (
  <span className="text-sm leading-6 font-normal text-black">{value}</span>
);
